import { useMemo } from 'react';
import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { parse, type OperationDefinitionNode } from 'graphql';
import axios from 'axios';

import { logRQGraphqlError } from 'services/log';

import useApi from './useApi';

export type CustomConfig = {
  variables?: Record<string, unknown>;
  queryKey: unknown[];
  logError?: boolean;
  origin?: string;
};

export type GqlResponse<TData> = { data: TData; errors?: Array<{ message?: string }> };

const useGqlQuery = <TQueryFnData = unknown, TData = TQueryFnData>(
  query: string,
  config: Omit<UseQueryOptions<TQueryFnData, Error, TData>, 'queryKey' | 'queryFn'> & CustomConfig,
) => {
  const api = useApi();

  if (import.meta.env.DEV) {
    if (config.queryKey == null) {
      throw new Error('Query key is required');
    }

    if (!Array.isArray(config.queryKey)) {
      throw new Error('Query key must be an array');
    }
  }

  const operationName = useMemo(() => {
    const parsedQuery = parse(query);

    const queryOperation = parsedQuery?.definitions?.filter?.((x) => x.kind === 'OperationDefinition');

    return (queryOperation?.[0] as OperationDefinitionNode | undefined)?.name?.value;
  }, [query]);

  const result = useQuery<TQueryFnData, Error, TData>({
    ...config,
    queryKey: config.queryKey.concat(query),

    queryFn: async ({ signal }) => {
      try {
        const res = await api.post<unknown, GqlResponse<TQueryFnData>>(
          `/graphql/${operationName}`,
          {
            query,
            variables: config.variables || {},
            operationName,
          },
          {
            signal,
          },
        );

        // case when the api returns 200 but the query response has errors
        if (res.errors?.length) {
          throw new Error(res.errors?.[0]?.message);
        }

        return res.data;
      } catch (error) {
        let thrownError: unknown;
        if (axios.isAxiosError(error)) {
          // Fall back to the original error when the response carries no GraphQL
          // errors array (e.g. network failures) so callers never receive `undefined`.
          thrownError = error.response?.data?.errors ?? error;
        } else {
          thrownError = error;
        }

        if (config.logError) {
          logRQGraphqlError({
            error: error as Error,
            origin: config.origin || `${operationName} Query`,
            variables: config.variables,
          });
        }

        throw thrownError;
      }
    },
  });

  if (import.meta.env.DEV) {
    return new Proxy(result, {
      get(target, prop) {
        if (prop === 'loading') {
          throw new Error('`loading` flag is deprecated, please use `isLoading`');
        }

        return target[prop as keyof typeof target];
      },
    }) as typeof result;
  }

  return result;
};

export default useGqlQuery;
